import { ArrowRight, Activity, Sparkles, Footprints, Cpu, Scan, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  subtitle: string;
  details: string;
  ctaText: string;
  href: string;
  icon: typeof Activity;
}
const services: ServiceItem[] = [{
  id: "physiotherapy",
  title: "Expert Physiotherapy",
  subtitle: "For pain, injury and rehabilitation",
  details: "Clinical assessment, treatment and structured rehabilitation for musculoskeletal problems, joint pain and sports injuries.",
  ctaText: "Explore Physiotherapy",
  href: "/ExpertPhysiotherapyPage",
  icon: Activity
}, {
  id: "recovery-massage",
  title: "Sports & Recovery Massage",
  subtitle: "For muscular tension and recovery",
  details: "Targeted soft-tissue treatment and recovery-focused massage for muscular tightness, training recovery and regular maintenance.",
  ctaText: "Explore Massage",
  href: "/RecoveryMassagePage",
  icon: Sparkles
}, {
  id: "gait-balance",
  title: "Gait & Balance Assessment",
  subtitle: "For walking, loading and balance",
  details: "Objective plantar-pressure measurement and balance testing providing clear data on movement symmetry, loading and stability.",
  ctaText: "Explore Gait & Balance",
  href: "/GaitBalanceAssessmentPage",
  icon: Footprints
}, {
  id: "biomechanics",
  title: "Specialist Biomechanics",
  subtitle: "For more detailed movement questions",
  details: "In-depth movement and pressure analysis for runners, cyclists and active individuals seeking precise mechanical insight.",
  ctaText: "Explore Biomechanics",
  href: "/BiomechanicsExpertisePage",
  icon: Cpu
}, {
  id: "orthotics",
  title: "Custom 3D-Printed Orthotics",
  subtitle: "For individually prescribed foot support",
  details: "Biomechanical assessment, pressure analysis and precision 3D foot scanning used to produce custom prescription orthotics.",
  ctaText: "Explore Orthotics",
  href: "/Custom3DPrintedOrthoticsPage",
  icon: Scan
}];
export default function Services() {
  return <section id="serviceslist" data-nav="light" className="relative scroll-mt-20 bg-background py-20 text-foreground md:py-28">
      {/* Anchor alias to support #services-batchwood links */}
      <span id="services-batchwood" className="sr-only" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            How can we help at Batchwood?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            PhysAnalytica brings physiotherapy and specialist biomechanics
            together at Batchwood Golf and Sports Centre in St Albans, with
            appointments ranging from injury rehabilitation and massage to
            detailed gait assessment and custom orthotics.
          </p>
        </div>

        {/* 5 Service Cards Grid */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {services.map((service, index) => {
          const Icon = service.icon;
          const isLastSpan = index === 4;
          return <div key={service.id} data-index={index} className={isLastSpan ? "md:col-span-2 lg:col-span-1 lg:col-start-2" : undefined}>
                <Card className="group relative flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-xs transition-all duration-200 hover:-translate-y-1 hover:border-chart-2/40 hover:shadow-md sm:p-8">
                  {/* Subtle top indicator bar */}
                  <div aria-hidden="true" className="absolute inset-x-8 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-transparent via-chart-2/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div>
                    <div className="mb-6 flex size-12 items-center justify-center rounded-xl bg-chart-2/10 text-chart-2 transition-colors duration-200 group-hover:bg-chart-2 group-hover:text-card">
                      <Icon className="size-6" />
                    </div>

                    <CardHeader className="p-0">
                      <CardTitle className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm font-medium text-chart-2">
                        {service.subtitle}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="mt-4 p-0">
                      <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {service.details}
                      </p>
                    </CardContent>
                  </div>

                  <CardFooter className="mt-8 p-0">
                    <Button asChild variant="outline" className="w-full justify-between rounded-lg border-border text-foreground hover:border-chart-2/50 hover:bg-chart-2/10 hover:text-foreground">
                      <Link to={service.href}>
                        <span>{service.ctaText}</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        {/* Notice Info Box */}
        <div className="mx-auto mt-12 flex max-w-3xl items-start gap-3.5 rounded-xl border border-border bg-muted/40 p-4 sm:mt-16 sm:p-6">
          <Info className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
            Availability varies by service and practitioner. Contact
            PhysAnalytica to confirm the appropriate St Albans appointment.
          </p>
        </div>
      </div>
    </section>;
}